import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  getFormBrandSettings,
  getFormLayoutSettings,
  getFormPlayerStyle,
  getFormThemeVariables,
} from '@/lib/form-theme'
import { isWelcomeScreenEnabled } from '@/lib/form-welcome-screen'
import {
  collectDisplayTargetQuestionIds,
  evaluateLogicJump,
  getNextQuestionId,
} from '@/lib/logic-evaluator'
import { shouldShowQuestionByConditionalDisplay } from '@/lib/dependent-choices'
import {
  MIAOLIN_CHANNEL_QUESTION_ID,
  buildMiaolinChannelTypeFallbackForm,
  buildMiaolinChannelTypeWebhookForm,
  fetchMiaolinChannelTypeOptions,
  getMiaolinChannelQuestionId,
  getMiaolinCustomerTypeQuestionIds,
  isMiaolinChannelWebhookQuestion,
  shouldHideEmptyMiaolinDynamicChannelTypeQuestion,
} from '@/lib/miaolin-channel-options'
import {
  type MiaolinCustomerCheckResult,
  checkMiaolinCustomerSubmission,
} from '@/lib/miaolin-customer-checker'
import {
  checkMiaolinCustomerDuplicate,
  fetchMiaolinCustomerDuplicateCheckConfig,
  getCustomerDuplicateCheckActionLabel,
  getCustomerDuplicateCheckFieldLabel,
  getCustomerDuplicateCheckPurpose,
} from '@/lib/miaolin-customer-duplicate-check'
import {
  type NewCustomerSubmitDuplicateComparisonRow,
  type NewCustomerSubmitDuplicatePreviewContract,
  buildNewCustomerSubmitDuplicatePreviewRequest,
  clearNewCustomerFormAnswers,
  createNewCustomerPendingQuestionnaireComparisonRows,
  createNewCustomerSubmitDuplicateComparisonRows,
  fetchNewCustomerPendingQuestionnaireDuplicatePreview,
  fetchNewCustomerSubmitDuplicatePreview,
  shouldHideCustomerShortNameDuplicateCheckButton,
} from '@/lib/miaolin-new-customer-submit-duplicate'
import {
  buildMiaolinDepartmentSalesWebhookForm,
  fetchMiaolinDepartmentSalesUserOptions,
  getMiaolinDepartmentQuestionId,
  getMiaolinDepartmentSalesQuestionIds,
  isMiaolinDepartmentWebhookQuestion,
} from '@/lib/miaolin-department-sales-options'
import {
  lookupMiaolinTaxIdCompanyTitle,
  normalizeMiaolinTaxIdInput,
} from '@/lib/miaolin-tax-id-lookup'
import { getQuestionValidationError } from '@/lib/question-validation'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { cn } from '@/lib/utils'
import { isApiDataClientMode } from '@/lib/data-client/mode'
import { useAuthStore } from '@/stores/authStore'
import type { FormPlayerMode, FormPlayerPhase } from '@/stores/formPlayerStore'
import { getFormProgressStorageKey, useFormPlayerStore } from '@/stores/formPlayerStore'
import { isLayoutQuestionType } from '@/types/form'
import type { AnswerValue, PublicForm, Question, QuestionGroupReference } from '@/types/form'
import { AlertCircle, CheckCircle2, Save, Search, X } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { BrandBanner, BrandChrome } from './BrandChrome'
import { NavigationButtons } from './NavigationButtons'
import { ProgressBar } from './ProgressBar'
import { QuestionRenderer } from './QuestionRenderer'
import { QuestionTransition } from './QuestionTransition'
import { ThankYouScreen } from './ThankYouScreen'
import { WelcomeScreen } from './WelcomeScreen'

export function FormPlayer({
  form,
  mode = 'live',
  initialPhase,
  showOnePageSubmitBar = true,
}: {
  form: PublicForm
  mode?: FormPlayerMode
  initialPhase?: Extract<FormPlayerPhase, 'welcome' | 'questions'>
  showOnePageSubmitBar?: boolean
}) {
  const state = useFormPlayerStore()
  const loadForm = useFormPlayerStore((store) => store.load)
  const replaceRuntimeForm = useFormPlayerStore((store) => store.replaceRuntimeForm)
  const refreshAutofillAnswers = useFormPlayerStore((store) => store.refreshAutofillAnswers)
  const clearAnswersByQuestionIds = useFormPlayerStore((store) => store.clearAnswersByQuestionIds)
  const authUserEmail = useAuthStore((store) => store.user?.email)
  const authUserName = useAuthStore((store) => store.user?.name)
  const startY = useRef(0)
  const channelTypeRequestId = useRef(0)
  const departmentSalesRequestId = useRef(0)
  const [dynamicOptionLoadingQuestionIds, setDynamicOptionLoadingQuestionIds] = useState<
    Set<string>
  >(() => new Set())

  function setQuestionsOptionLoading(questionIds: string[], loading: boolean) {
    if (questionIds.length === 0) return
    setDynamicOptionLoadingQuestionIds((current) => {
      const next = new Set(current)
      for (const questionId of questionIds) {
        if (loading) next.add(questionId)
        else next.delete(questionId)
      }
      return next
    })
  }

  useLayoutEffect(() => {
    loadForm(form, { mode, initialPhase })
  }, [form, initialPhase, loadForm, mode])

  useEffect(() => {
    refreshAutofillAnswers()
  }, [authUserEmail, authUserName, refreshAutofillAnswers])

  useEffect(() => {
    const root = document.documentElement
    for (const [property, value] of Object.entries(getFormThemeVariables(form.themeConfig))) {
      root.style.setProperty(property, String(value))
    }
    document.title = form.title

    const faviconUrl = getFormBrandSettings(form.settings).faviconUrl
    if (faviconUrl) {
      let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      if (!favicon) {
        favicon = document.createElement('link')
        favicon.rel = 'icon'
        document.head.appendChild(favicon)
      }
      favicon.href = faviconUrl
    }
  }, [form])

  async function handleMiaolinChannelChange(value: AnswerValue) {
    const channel = typeof value === 'string' ? value.trim() : ''
    const requestId = channelTypeRequestId.current + 1
    channelTypeRequestId.current = requestId
    const customerTypeQuestionIds = getMiaolinCustomerTypeQuestionIds(
      useFormPlayerStore.getState().questions,
    )

    clearAnswersByQuestionIds(customerTypeQuestionIds)
    replaceRuntimeForm(buildMiaolinChannelTypeFallbackForm(form))
    if (!channel) {
      setQuestionsOptionLoading(customerTypeQuestionIds, false)
      return
    }

    setQuestionsOptionLoading(customerTypeQuestionIds, true)
    try {
      const result = await fetchMiaolinChannelTypeOptions({ formId: form.id, channel })
      if (requestId !== channelTypeRequestId.current) return

      const latestQuestions = useFormPlayerStore.getState().questions
      const channelQuestionId =
        getMiaolinChannelQuestionId(latestQuestions) ?? MIAOLIN_CHANNEL_QUESTION_ID
      const latestChannel = String(
        useFormPlayerStore.getState().answers.get(channelQuestionId) ?? '',
      ).trim()
      if (latestChannel !== channel || !result?.options.length) return

      replaceRuntimeForm(
        buildMiaolinChannelTypeWebhookForm({
          baseForm: form,
          channel,
          options: result.options,
        }),
      )
    } finally {
      if (requestId === channelTypeRequestId.current) {
        setQuestionsOptionLoading(customerTypeQuestionIds, false)
      }
    }
  }

  async function handleMiaolinDepartmentChange(value: AnswerValue) {
    const department = typeof value === 'string' ? value.trim() : ''
    const requestId = departmentSalesRequestId.current + 1
    departmentSalesRequestId.current = requestId

    const latestForm = useFormPlayerStore.getState().form ?? form
    const departmentSalesQuestionIds = getMiaolinDepartmentSalesQuestionIds(latestForm)
    clearAnswersByQuestionIds(departmentSalesQuestionIds)
    if (!department) {
      setQuestionsOptionLoading(departmentSalesQuestionIds, false)
      return
    }

    setQuestionsOptionLoading(departmentSalesQuestionIds, true)
    try {
      const result = await fetchMiaolinDepartmentSalesUserOptions({ formId: form.id, department })
      if (requestId !== departmentSalesRequestId.current) return

      const latestQuestions = useFormPlayerStore.getState().questions
      const departmentQuestionId = getMiaolinDepartmentQuestionId(latestQuestions) ?? 'q-miaolin-24'
      const latestDepartment = String(
        useFormPlayerStore.getState().answers.get(departmentQuestionId) ?? '',
      ).trim()
      if (latestDepartment !== department || !result?.options.length) return

      replaceRuntimeForm(
        buildMiaolinDepartmentSalesWebhookForm({
          baseForm: useFormPlayerStore.getState().form ?? form,
          department,
          options: result.options,
        }),
      )
    } finally {
      if (requestId === departmentSalesRequestId.current) {
        setQuestionsOptionLoading(departmentSalesQuestionIds, false)
      }
    }
  }

  if (state.phase === 'welcome' && isWelcomeScreenEnabled(form)) {
    return (
      <WelcomeScreen
        form={form}
        previewMode={mode === 'preview'}
        onStart={() => useFormPlayerStore.setState({ phase: 'questions' })}
      />
    )
  }

  if (state.phase === 'thankyou') {
    return (
      <ThankYouScreen
        form={form}
        previewMode={mode === 'preview'}
        onRestart={() => {
          if (mode === 'live') localStorage.removeItem(getFormProgressStorageKey(form.id))
          state.load(form, { mode, initialPhase })
        }}
      />
    )
  }

  const currentQuestion = state.questions[state.currentIndex]
  if (!currentQuestion) return null
  const showProgress = form.settings.showProgress !== false
  const layout = getFormLayoutSettings(form.settings)
  const fillMode = form.settings.fillMode === 'step' ? 'step' : 'one_page'
  const currentInlineValidationError = getInlineQuestionValidationError(
    currentQuestion,
    state.answers.get(currentQuestion.id),
  )

  if (fillMode === 'one_page') {
    return (
      <OnePageFormPlayer
        form={form}
        mode={mode}
        layout={layout}
        showProgress={showProgress}
        showSubmitBar={showOnePageSubmitBar}
        onMiaolinChannelChange={handleMiaolinChannelChange}
        onMiaolinDepartmentChange={handleMiaolinDepartmentChange}
        dynamicOptionLoadingQuestionIds={dynamicOptionLoadingQuestionIds}
      />
    )
  }

  const isCurrentDynamicOptionLoading = dynamicOptionLoadingQuestionIds.has(currentQuestion.id)

  return (
    <main
      className={
        mode === 'preview'
          ? 'form-player-theme relative min-h-full px-5'
          : 'form-player-theme relative min-h-screen px-5'
      }
      style={{
        ...getFormPlayerStyle(form.themeConfig, form.settings),
        paddingBlock: 'var(--form-page-padding-block,4rem)',
      }}
      onTouchStart={(event) => {
        startY.current = event.touches[0].clientY
      }}
      onTouchEnd={(event) => {
        const delta = event.changedTouches[0].clientY - startY.current
        if (delta < -80) void state.goNext()
        if (delta > 80) state.goBack()
      }}
    >
      <BrandChrome form={form} contained={mode === 'preview'} />
      <div
        className="mx-auto flex w-full flex-col"
        style={{
          justifyContent: layout.verticalAlign === 'top' ? 'flex-start' : 'center',
          maxWidth: 'var(--form-max-width, 720px)',
          minHeight:
            'calc(100vh - (var(--form-page-padding-block,4rem) + var(--form-page-padding-block,4rem)))',
          textAlign: layout.questionAlign,
        }}
      >
        <QuestionTransition id={currentQuestion.id} direction={state.direction}>
          <div>
            <BrandBanner form={form} surface="questions" />
            {layout.showQuestionNumber && (
              <div
                className="mb-4 text-sm font-medium"
                style={{
                  color: 'var(--form-answer,#64748B)',
                  fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
                  fontSize: 'var(--form-meta-size,0.9375rem)',
                }}
              >
                {state.currentIndex + 1} / {state.questions.length}
              </div>
            )}
            <QuestionTitleRow
              question={currentQuestion}
              answers={state.answers}
              questions={state.questions}
            />
            {currentQuestion.description && (
              <p
                className="mt-3"
                style={{
                  color: 'var(--form-answer,#475569)',
                  fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
                  fontSize: 'var(--form-body-size,1.125rem)',
                  lineHeight: 'var(--form-line-height,1.55)',
                }}
              >
                {currentQuestion.description}
              </p>
            )}
            <div style={{ marginTop: 'var(--form-question-block-gap,2rem)' }}>
              <QuestionRenderer
                question={currentQuestion}
                value={state.answers.get(currentQuestion.id)}
                answers={state.answers}
                onChange={(value) => {
                  state.setAnswer(currentQuestion.id, value)
                  if (isMiaolinChannelWebhookQuestion(currentQuestion)) {
                    void handleMiaolinChannelChange(value)
                  }
                  if (isMiaolinDepartmentWebhookQuestion(currentQuestion)) {
                    void handleMiaolinDepartmentChange(value)
                  }
                }}
                onSubmit={() => void state.goNext()}
                autoFocus
                disabled={isCurrentDynamicOptionLoading}
                loadingMessage={isCurrentDynamicOptionLoading ? '更新資訊中' : null}
                theme={form.themeConfig}
              />
              <InlineValidationError
                questionId={currentQuestion.id}
                message={currentInlineValidationError}
              />
            </div>
            {state.validationError && !currentInlineValidationError && (
              <div className="mt-4 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                <AlertCircle className="size-4" />
                {state.validationError}
              </div>
            )}
            <NavigationButtons
              canGoBack={state.currentIndex > 0}
              layout={layout.navigationLayout}
              questionAlign={layout.questionAlign}
              showBackButton={layout.showBackButton}
              isSubmitting={state.isSubmitting}
              onBack={state.goBack}
              onNext={() => void state.goNext()}
            />
          </div>
        </QuestionTransition>
      </div>
      {showProgress && (
        <ProgressBar
          completed={state.currentIndex + 1}
          total={state.questions.length}
          position={layout.progressPosition}
          style={form.themeConfig.progressStyle}
        />
      )}
    </main>
  )
}

function OnePageFormPlayer({
  form,
  mode,
  layout,
  showProgress,
  showSubmitBar,
  onMiaolinChannelChange,
  onMiaolinDepartmentChange,
  dynamicOptionLoadingQuestionIds,
}: {
  form: PublicForm
  mode: FormPlayerMode
  layout: ReturnType<typeof getFormLayoutSettings>
  showProgress: boolean
  showSubmitBar: boolean
  onMiaolinChannelChange: (value: AnswerValue) => Promise<void>
  onMiaolinDepartmentChange: (value: AnswerValue) => Promise<void>
  dynamicOptionLoadingQuestionIds: Set<string>
}) {
  const state = useFormPlayerStore()
  const systemFeatureVisibility = useSystemFeatureVisibility()
  const activeForm = state.form ?? form
  const [customerCheckDialog, setCustomerCheckDialog] = useState<MiaolinCustomerCheckResult | null>(
    null,
  )
  const [pendingQuestionnaireDuplicateDialog, setPendingQuestionnaireDuplicateDialog] = useState<{
    result: NewCustomerSubmitDuplicatePreviewContract
    rows: NewCustomerSubmitDuplicateComparisonRow[]
    nextAnswers: Map<string, AnswerValue>
  } | null>(null)
  const [newCustomerDuplicateDialog, setNewCustomerDuplicateDialog] = useState<{
    result: NewCustomerSubmitDuplicatePreviewContract
    rows: NewCustomerSubmitDuplicateComparisonRow[]
    nextAnswers: Map<string, AnswerValue>
  } | null>(null)
  const [newCustomerDuplicateNotice, setNewCustomerDuplicateNotice] = useState<{
    title: string
    message: string
  } | null>(null)
  const [keepPendingQuestionnaireConfirmOpen, setKeepPendingQuestionnaireConfirmOpen] =
    useState(false)
  const [keepExistingConfirmOpen, setKeepExistingConfirmOpen] = useState(false)
  const [missingRequiredQuestion, setMissingRequiredQuestion] = useState<{
    id: string
    title: string
    message: string
  } | null>(null)
  const [requiredDialogOpen, setRequiredDialogOpen] = useState(false)
  const lastServerValidationQuestionRef = useRef<string | null>(null)
  const visibleQuestions = getOnePageVisibleQuestions(
    state.questions,
    activeForm.logicJumps,
    state.answers,
    activeForm.hiddenFields,
    (activeForm as PublicForm & { groups?: QuestionGroupReference[] }).groups ?? [],
    dynamicOptionLoadingQuestionIds,
  )
  const visibleQuestionIds = new Set(visibleQuestions.map((question) => question.id))
  const hasVisibleInlineValidationError = visibleQuestions.some((question) =>
    Boolean(getInlineQuestionValidationError(question, state.answers.get(question.id))),
  )
  const showCustomerCompareSubmitButton =
    systemFeatureVisibility['forms.customerSubmitCustomerCompareFlow']
  const answeredCount = visibleQuestions.filter((question) => {
    if (isLayoutQuestionType(question.type)) return true
    const value = state.answers.get(question.id)
    return !getQuestionValidationError({ ...question, required: true }, value)
  }).length

  function submitFilteredAnswers(
    nextAnswers: Map<string, AnswerValue>,
    metadataPatch: Record<string, unknown> | null = null,
  ) {
    const currentState = useFormPlayerStore.getState()
    if (nextAnswers.size !== currentState.answers.size) {
      useFormPlayerStore.setState({ answers: nextAnswers })
    }
    useFormPlayerStore.getState().setSubmissionMetadataPatch(metadataPatch)
    void useFormPlayerStore.getState().submit()
  }

  function getFilteredVisibleAnswers() {
    const currentState = useFormPlayerStore.getState()
    return new Map(
      Array.from(currentState.answers.entries()).filter(([questionId]) =>
        visibleQuestionIds.has(questionId),
      ),
    )
  }

  function scrollToQuestion(questionId: string) {
    const section = document.getElementById(`question-${questionId}`)
    section?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const firstControl = section?.querySelector<HTMLElement>(
      'input, textarea, select, button, [tabindex]:not([tabindex="-1"])',
    )
    firstControl?.focus({ preventScroll: true })
  }

  useEffect(() => {
    if (mode !== 'preview' || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        const currentEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0]
        const questionId = currentEntry?.target.getAttribute('data-preview-question-id')
        if (!questionId) return

        const nextIndex = useFormPlayerStore
          .getState()
          .questions.findIndex((question) => question.id === questionId)
        if (nextIndex < 0 || useFormPlayerStore.getState().currentIndex === nextIndex) return
        useFormPlayerStore.setState({ currentIndex: nextIndex })
      },
      {
        root: null,
        rootMargin: '-20% 0px -55% 0px',
        threshold: [0.2, 0.45, 0.7],
      },
    )

    for (const question of visibleQuestions) {
      const section = document.getElementById(`question-${question.id}`)
      if (section) observer.observe(section)
    }

    return () => observer.disconnect()
  }, [mode, visibleQuestions])

  useEffect(() => {
    const questionId = state.validationErrorQuestionId
    if (!questionId) {
      lastServerValidationQuestionRef.current = null
      return
    }

    const key = `${questionId}:${state.validationError ?? ''}`
    if (lastServerValidationQuestionRef.current === key) return

    const question = visibleQuestions.find((item) => item.id === questionId)
    if (!question) return

    const prefix = `${question.title}：`
    const serverMessage = state.validationError?.startsWith(prefix)
      ? state.validationError.slice(prefix.length).trim()
      : null
    const message =
      serverMessage ||
      getQuestionValidationError(question, state.answers.get(question.id)) ||
      '請完成此題'

    lastServerValidationQuestionRef.current = key
    setMissingRequiredQuestion({
      id: question.id,
      title: question.title,
      message,
    })
    setRequiredDialogOpen(true)
    window.setTimeout(() => scrollToQuestion(question.id), 0)
  }, [state.validationError, state.validationErrorQuestionId, visibleQuestions, state.answers])

  function handleQuestionChange(question: Question, value: AnswerValue) {
    state.setAnswer(question.id, value)
    if (isMiaolinChannelWebhookQuestion(question)) {
      void onMiaolinChannelChange(value)
    }
    if (isMiaolinDepartmentWebhookQuestion(question)) {
      void onMiaolinDepartmentChange(value)
    }
    if (
      missingRequiredQuestion?.id === question.id &&
      !getQuestionValidationError(question, value)
    ) {
      setMissingRequiredQuestion(null)
      setRequiredDialogOpen(false)
    }
  }

  async function submitVisibleQuestions(
    duplicateFlow: 'pending_questionnaire' | 'customer_compare' = 'pending_questionnaire',
  ) {
    const loadingQuestion = visibleQuestions.find((question) =>
      dynamicOptionLoadingQuestionIds.has(question.id),
    )
    if (loadingQuestion) {
      useFormPlayerStore.setState({
        validationError: `${loadingQuestion.title}：選項更新中，請稍候再選擇。`,
      })
      setMissingRequiredQuestion({
        id: loadingQuestion.id,
        title: loadingQuestion.title,
        message: '選項更新中，請稍候再選擇。',
      })
      setRequiredDialogOpen(true)
      scrollToQuestion(loadingQuestion.id)
      return
    }

    const missingQuestion = visibleQuestions.find((question) =>
      getQuestionValidationError(question, state.answers.get(question.id)),
    )
    if (missingQuestion) {
      const message =
        getQuestionValidationError(missingQuestion, state.answers.get(missingQuestion.id)) ??
        '請完成此題'
      const inlineValidationError = getInlineQuestionValidationError(
        missingQuestion,
        state.answers.get(missingQuestion.id),
      )
      useFormPlayerStore.setState({
        validationError: `${missingQuestion.title}：${message}`,
      })
      setMissingRequiredQuestion({
        id: missingQuestion.id,
        title: missingQuestion.title,
        message,
      })
      setRequiredDialogOpen(!inlineValidationError)
      scrollToQuestion(missingQuestion.id)
      return
    }

    const conditionalMissingQuestion = findMiaolinConditionalRequiredQuestion(
      activeForm,
      visibleQuestions,
      state.answers,
    )
    if (conditionalMissingQuestion) {
      const { question, message } = conditionalMissingQuestion
      useFormPlayerStore.setState({
        validationError: `${question.title}：${message}`,
      })
      setMissingRequiredQuestion({
        id: question.id,
        title: question.title,
        message,
      })
      setRequiredDialogOpen(true)
      scrollToQuestion(question.id)
      return
    }

    const nextAnswers = getFilteredVisibleAnswers()
    const checkResult = checkMiaolinCustomerSubmission(activeForm, nextAnswers)
    if (checkResult.status === 'duplicate_found' || checkResult.status === 'existing_missing') {
      if (nextAnswers.size !== useFormPlayerStore.getState().answers.size) {
        useFormPlayerStore.setState({ answers: nextAnswers })
      }
      setCustomerCheckDialog(checkResult)
      return
    }

    const duplicatePreviewRequest = buildNewCustomerSubmitDuplicatePreviewRequest(
      visibleQuestions,
      nextAnswers,
    )
    if (
      duplicateFlow === 'pending_questionnaire' &&
      mode === 'live' &&
      isApiDataClientMode() &&
      duplicatePreviewRequest.enabled
    ) {
      try {
        const result = await fetchNewCustomerPendingQuestionnaireDuplicatePreview({
          formId: activeForm.id,
          request: duplicatePreviewRequest,
        })
        if (result.status === 'pending_questionnaire_exists' && result.pendingRecord) {
          setPendingQuestionnaireDuplicateDialog({
            result,
            rows: createNewCustomerPendingQuestionnaireComparisonRows(
              visibleQuestions,
              nextAnswers,
              result.pendingRecord,
            ),
            nextAnswers,
          })
          return
        }
      } catch (error) {
        console.error('[MiaoForm] pending questionnaire duplicate preview failed', error)
        setNewCustomerDuplicateNotice({
          title: '未審核問卷比對暫時無法完成',
          message: '目前無法確認是否已有相同未審核問卷資料，請稍後再送出。',
        })
        return
      }
    }

    if (
      duplicateFlow === 'customer_compare' &&
      mode === 'live' &&
      isApiDataClientMode() &&
      duplicatePreviewRequest.enabled
    ) {
      try {
        const result = await fetchNewCustomerSubmitDuplicatePreview({
          formId: activeForm.id,
          request: duplicatePreviewRequest,
        })
        if (result.status === 'erp_customer_exists') {
          setNewCustomerDuplicateNotice({
            title: '已存在ERP客戶',
            message:
              '系統已依統一編號、市話或手機找到已建檔的 ERP 客戶，請改用既有客戶變更資料流程。',
          })
          return
        }
        if (result.status === 'project_customer_exists' && result.customer) {
          setNewCustomerDuplicateDialog({
            result,
            rows: createNewCustomerSubmitDuplicateComparisonRows(
              visibleQuestions,
              nextAnswers,
              result.customer,
            ),
            nextAnswers,
          })
          return
        }
      } catch (error) {
        console.error('[MiaoForm] new customer duplicate preview failed', error)
        setNewCustomerDuplicateNotice({
          title: '客戶資料比對暫時無法完成',
          message: '目前無法確認是否已有相同客戶資料，請稍後再送出。',
        })
        return
      }
    }

    submitFilteredAnswers(nextAnswers)
  }

  return (
    <main
      className={
        mode === 'preview'
          ? 'form-player-theme relative min-h-full'
          : 'form-player-theme relative min-h-screen'
      }
      style={{
        ...getFormPlayerStyle(form.themeConfig, form.settings),
        paddingBlock: 'var(--form-page-padding-block,4rem)',
      }}
    >
      <BrandChrome form={form} contained={mode === 'preview'} />
      <div className="mx-auto w-full px-5" style={{ maxWidth: 'var(--form-max-width, 760px)' }}>
        <BrandBanner form={form} surface="questions" />
        <div
          className="space-y-10"
          style={{
            textAlign: layout.questionAlign,
          }}
        >
          {visibleQuestions.map((question, index) => {
            const inlineValidationError = getInlineQuestionValidationError(
              question,
              state.answers.get(question.id),
            )
            const isFocusedValidationQuestion = missingRequiredQuestion?.id === question.id
            const isMissingRequired = isFocusedValidationQuestion && !inlineValidationError
            const hasValidationHighlight = isMissingRequired || Boolean(inlineValidationError)
            const inlineValidationId = inlineValidationError
              ? `question-${question.id}-inline-error`
              : undefined
            const isDynamicOptionLoading = dynamicOptionLoadingQuestionIds.has(question.id)
            return (
              <section
                key={question.id}
                id={`question-${question.id}`}
                data-preview-question-id={mode === 'preview' ? question.id : undefined}
                data-required-missing={isMissingRequired ? 'true' : undefined}
                data-inline-validation-error={inlineValidationError ? 'true' : undefined}
                aria-invalid={hasValidationHighlight || undefined}
                aria-describedby={inlineValidationId}
                className={`rounded-lg border p-5 shadow-sm backdrop-blur transition-[box-shadow,border-color,background-color] ${
                  hasValidationHighlight
                    ? 'ring-2 ring-rose-500 ring-offset-2 ring-offset-white'
                    : ''
                }`}
                style={{
                  background: hasValidationHighlight
                    ? 'color-mix(in srgb, #fff1f2 78%, var(--form-panel-bg,rgba(255,255,255,0.78)))'
                    : 'var(--form-panel-bg,rgba(255,255,255,0.78))',
                  borderColor: hasValidationHighlight
                    ? '#fb7185'
                    : 'var(--form-panel-border,rgba(255,255,255,0.32))',
                }}
              >
                {!isLayoutQuestionType(question.type) && layout.showQuestionNumber ? (
                  <div
                    className="mb-3 text-sm font-medium"
                    style={{
                      color: 'var(--form-answer,#64748B)',
                      fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
                      fontSize: 'var(--form-meta-size,0.9375rem)',
                    }}
                  >
                    {index + 1} / {visibleQuestions.length}
                  </div>
                ) : null}
                {!isLayoutQuestionType(question.type) ? (
                  <>
                    <QuestionTitleRow
                      question={question}
                      answers={state.answers}
                      questions={state.questions}
                    />
                    {question.description ? (
                      <p
                        className="mt-3"
                        style={{
                          color: 'var(--form-answer,#475569)',
                          fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
                          fontSize: 'var(--form-body-size,1.125rem)',
                          lineHeight: 'var(--form-line-height,1.55)',
                        }}
                      >
                        {question.description}
                      </p>
                    ) : null}
                  </>
                ) : null}
                <div
                  style={{
                    marginTop: isLayoutQuestionType(question.type)
                      ? 0
                      : 'var(--form-question-block-gap,2rem)',
                  }}
                >
                  <QuestionRenderer
                    question={question}
                    value={state.answers.get(question.id)}
                    answers={state.answers}
                    onChange={(value) => handleQuestionChange(question, value)}
                    onSubmit={() => undefined}
                    showInlineSubmit={false}
                    autoFocus={index === 0}
                    disabled={isDynamicOptionLoading}
                    loadingMessage={isDynamicOptionLoading ? '更新資訊中' : null}
                    theme={form.themeConfig}
                  />
                  <InlineValidationError
                    questionId={question.id}
                    message={inlineValidationError}
                    id={inlineValidationId}
                  />
                </div>
                {isMissingRequired ? (
                  <div className="mt-4 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    <AlertCircle className="size-4 shrink-0" />
                    {missingRequiredQuestion.title}：{missingRequiredQuestion.message}
                  </div>
                ) : null}
              </section>
            )
          })}
        </div>
        {state.validationError && !hasVisibleInlineValidationError && !missingRequiredQuestion ? (
          <div className="mt-5 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            <AlertCircle className="size-4" />
            {state.validationError}
          </div>
        ) : null}
        {showSubmitBar ? (
          <div
            className="sticky bottom-0 mt-8 rounded-t-xl border p-3 shadow-[0_-12px_30px_rgba(15,23,42,0.08)] backdrop-blur"
            style={{
              background: 'var(--form-panel-bg,rgba(255,255,255,0.95))',
              borderColor: 'var(--form-panel-border,rgba(203,213,225,0.82))',
            }}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div
                className="flex min-w-0 items-start gap-3 rounded-lg border px-3 py-2 text-sm"
                data-testid="one-page-submit-status"
                style={{
                  background: 'var(--form-control-bg,#f8fafc)',
                  borderColor: 'var(--form-control-border,#e2e8f0)',
                  color: 'var(--form-muted,#475569)',
                }}
              >
                <div
                  className="grid size-8 shrink-0 place-items-center rounded-md"
                  style={{
                    background: 'color-mix(in srgb, var(--form-accent,#10B981) 18%, transparent)',
                    color: 'var(--form-label,#047857)',
                  }}
                >
                  <CheckCircle2 className="size-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium" style={{ color: 'var(--form-text,#0f172a)' }}>
                    填寫內容會在正式送出前暫存
                  </div>
                  <div
                    className="mt-0.5 flex items-center gap-1.5 text-xs leading-5"
                    style={{ color: 'var(--form-muted,#64748b)' }}
                  >
                    <Save className="size-3.5 shrink-0" />
                    正式送出前會暫存在此瀏覽器，送出後才會清除。
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                {showCustomerCompareSubmitButton ? (
                  <Button
                    type="button"
                    size="lg"
                    variant="outline"
                    className="sm:min-w-48"
                    disabled={state.isSubmitting}
                    onClick={() => void submitVisibleQuestions('customer_compare')}
                  >
                    送出問卷(比對客戶流程)
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="lg"
                  className="sm:min-w-36"
                  disabled={state.isSubmitting}
                  onClick={() => void submitVisibleQuestions()}
                >
                  {state.isSubmitting ? '送出中...' : '送出問卷'}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {showProgress && (
        <ProgressBar
          completed={Math.min(answeredCount, visibleQuestions.length)}
          total={visibleQuestions.length}
          position={layout.progressPosition}
          style={form.themeConfig.progressStyle}
        />
      )}
      <MiaolinCustomerCheckDialog
        result={customerCheckDialog}
        onContinueEditing={() => setCustomerCheckDialog(null)}
        onConfirmSubmit={() => {
          setCustomerCheckDialog(null)
          submitFilteredAnswers(getFilteredVisibleAnswers())
        }}
      />
      <NewCustomerDuplicateNoticeDialog
        notice={newCustomerDuplicateNotice}
        onClose={() => setNewCustomerDuplicateNotice(null)}
      />
      <NewCustomerDuplicateReviewDialog
        dataTestId="new-customer-duplicate-review-dialog"
        dialog={newCustomerDuplicateDialog}
        title="比對客戶資料"
        description="系統已依統一編號、市話或手機找到專案內既有客戶資料。請確認要維持既有資料，或比對後仍送出本次問卷。"
        closeLabel="關閉比對客戶資料"
        comparedColumnLabel="客戶資料"
        keepButtonLabel="保留客戶資料"
        onContinueEditing={() => setNewCustomerDuplicateDialog(null)}
        onKeepExisting={() => setKeepExistingConfirmOpen(true)}
        onConfirmSubmit={() => {
          const comparedCustomer = newCustomerDuplicateDialog?.result.customer
          if (!newCustomerDuplicateDialog || !comparedCustomer) return
          const result = newCustomerDuplicateDialog.result
          setNewCustomerDuplicateDialog(null)
          submitFilteredAnswers(newCustomerDuplicateDialog.nextAnswers, {
            duplicateSignals: [
              {
                field: result.matchedFields[0] ?? 'taxId',
                value: resolveNewCustomerDuplicateSignalValue(
                  result.matchedFields[0],
                  newCustomerDuplicateDialog.nextAnswers,
                  visibleQuestions,
                ),
                status: 'duplicate_found',
                isDuplicate: true,
                matchedCustomerId: comparedCustomer.id,
                matchedCustomerName: comparedCustomer.companyName,
                matchedCustomerCode: comparedCustomer.erpCustomerCode || undefined,
                confidence: 100,
                source: 'd1_new_customer_submit_duplicate_preview',
                safeMessage: '使用者已比對既有專案客戶資料後確認送出全新客戶問卷。',
                archiveMatchedCustomerOnApproval: true,
              },
            ],
            newCustomerDuplicateSubmitReview: {
              action: 'submit_new_customer_after_comparison',
              comparedCustomerId: comparedCustomer.id,
              matchedFields: result.matchedFields,
              archiveMatchedCustomerOnApproval: true,
              source: 'd1_new_customer_submit_duplicate_preview',
            },
          })
        }}
      />
      <NewCustomerDuplicateReviewDialog
        dataTestId="pending-questionnaire-duplicate-review-dialog"
        dialog={pendingQuestionnaireDuplicateDialog}
        title="比對未審核問卷資料"
        description="系統已依統一編號、市話或手機找到相同的未審核問卷資料。請確認要保留未審核資料，或比對後仍送出本次問卷。"
        closeLabel="關閉比對未審核問卷資料"
        comparedColumnLabel="未審核問卷"
        keepButtonLabel="保留未審核資料"
        onContinueEditing={() => setPendingQuestionnaireDuplicateDialog(null)}
        onKeepExisting={() => setKeepPendingQuestionnaireConfirmOpen(true)}
        onConfirmSubmit={() => {
          const result = pendingQuestionnaireDuplicateDialog?.result
          if (!pendingQuestionnaireDuplicateDialog || !result?.pendingRecord) return
          setPendingQuestionnaireDuplicateDialog(null)
          submitFilteredAnswers(pendingQuestionnaireDuplicateDialog.nextAnswers, {
            pendingQuestionnaireDuplicateSubmitReview: {
              action: 'submit_new_customer_after_pending_questionnaire_comparison',
              comparedSourceRecordId: result.pendingRecord.id,
              comparedResponseId: result.pendingRecord.responseId,
              comparedApplicationId: result.pendingRecord.applicationId,
              matchedFields: result.matchedFields,
              archiveMatchedPendingSourceRecordsOnSubmit: true,
              archiveTargets: result.archiveTargets ?? [],
              source: 'd1_pending_questionnaire_submit_duplicate_preview',
            },
          })
        }}
      />
      <NewCustomerDuplicateKeepExistingConfirmDialog
        open={keepExistingConfirmOpen}
        onOpenChange={setKeepExistingConfirmOpen}
        title="保留客戶資料"
        description="是否保留客戶資料, 並清除目前填寫的問卷資料"
        onConfirm={() => {
          if (!newCustomerDuplicateDialog) return
          const nextAnswers = clearNewCustomerFormAnswers(
            visibleQuestions,
            newCustomerDuplicateDialog.nextAnswers,
          )
          if (mode === 'live') localStorage.removeItem(getFormProgressStorageKey(activeForm.id))
          useFormPlayerStore.setState({
            answers: nextAnswers,
            validationError: null,
            validationErrorQuestionId: null,
          })
          setNewCustomerDuplicateDialog(null)
          setKeepExistingConfirmOpen(false)
          window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0)
        }}
      />
      <NewCustomerDuplicateKeepExistingConfirmDialog
        open={keepPendingQuestionnaireConfirmOpen}
        onOpenChange={setKeepPendingQuestionnaireConfirmOpen}
        title="保留未審核資料"
        description="是否保留未審核資料, 並清除目前填寫的問卷資料"
        onConfirm={() => {
          if (!pendingQuestionnaireDuplicateDialog) return
          const nextAnswers = clearNewCustomerFormAnswers(
            visibleQuestions,
            pendingQuestionnaireDuplicateDialog.nextAnswers,
          )
          if (mode === 'live') localStorage.removeItem(getFormProgressStorageKey(activeForm.id))
          useFormPlayerStore.setState({
            answers: nextAnswers,
            validationError: null,
            validationErrorQuestionId: null,
          })
          setPendingQuestionnaireDuplicateDialog(null)
          setKeepPendingQuestionnaireConfirmOpen(false)
          window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0)
        }}
      />
      <RequiredQuestionDialog
        missingQuestion={missingRequiredQuestion}
        open={requiredDialogOpen}
        onOpenChange={setRequiredDialogOpen}
        onGoToQuestion={() => {
          setRequiredDialogOpen(false)
          if (missingRequiredQuestion) scrollToQuestion(missingRequiredQuestion.id)
        }}
      />
    </main>
  )
}

function getInlineQuestionValidationError(question: Question, value: AnswerValue | undefined) {
  if (question.type === 'TAIWAN_ADDRESS') return null
  return getQuestionValidationError({ ...question, required: false }, value)
}

function findMiaolinConditionalRequiredQuestion(
  form: PublicForm,
  visibleQuestions: Question[],
  answers: Map<string, AnswerValue>,
) {
  if (!isMiaolinFullNewCustomerFlow(form.questions, answers)) return null

  const channelQuestionId = getMiaolinChannelQuestionId(form.questions) ?? MIAOLIN_CHANNEL_QUESTION_ID
  if (!isAnswerValueEmpty(answers.get(channelQuestionId))) {
    const typeQuestionIds = new Set(getMiaolinCustomerTypeQuestionIds(form.questions))
    const customerTypeQuestion = visibleQuestions.find(
      (question) =>
        typeQuestionIds.has(question.id) ||
        question.settings.customerFieldTarget === 'customerType' ||
        question.title.trim() === '型態別',
    )
    if (customerTypeQuestion && isAnswerValueEmpty(answers.get(customerTypeQuestion.id))) {
      return { question: customerTypeQuestion, message: '請選擇型態別' }
    }
  }

  const departmentQuestionId = getMiaolinDepartmentQuestionId(form.questions)
  if (departmentQuestionId && !isAnswerValueEmpty(answers.get(departmentQuestionId))) {
    const departmentSalesQuestionIds = new Set(getMiaolinDepartmentSalesQuestionIds(form))
    const salesQuestion = visibleQuestions.find(
      (question) =>
        departmentSalesQuestionIds.has(question.id) && !isLayoutQuestionType(question.type),
    )
    if (salesQuestion && isAnswerValueEmpty(answers.get(salesQuestion.id))) {
      return { question: salesQuestion, message: '請選擇業務' }
    }
  }

  return null
}

function isMiaolinFullNewCustomerFlow(questions: Question[], answers: Map<string, AnswerValue>) {
  const formTypeQuestion = questions.find(
    (question) =>
      question.settings.customerFieldTarget === 'formType' ||
      question.settings.dbFieldName === 'form_type' ||
      question.title.trim() === '新客戶建檔選擇',
  )
  if (!formTypeQuestion) return false
  const value = answers.get(formTypeQuestion.id)
  if (typeof value === 'string') return value.includes('全新客戶')
  if (Array.isArray(value)) return value.some((item) => String(item).includes('全新客戶'))
  return false
}

function isAnswerValueEmpty(value: AnswerValue | undefined) {
  if (value === undefined || value === null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') {
    return !Object.values(value).some((item) => {
      if (item === undefined || item === null) return false
      if (typeof item === 'string') return item.trim() !== ''
      if (Array.isArray(item)) return item.length > 0
      return true
    })
  }
  return false
}

function InlineValidationError({
  questionId,
  message,
  id = `question-${questionId}-inline-error`,
}: {
  questionId: string
  message: string | null
  id?: string
}) {
  if (!message) return null

  return (
    <div
      id={id}
      role="alert"
      aria-live="polite"
      data-testid={`inline-validation-error-${questionId}`}
      className="mt-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm leading-6 text-rose-700"
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <span>{message}</span>
    </div>
  )
}

function QuestionTitleRow({
  question,
  answers,
  questions,
}: {
  question: Question
  answers: Map<string, AnswerValue>
  questions: Question[]
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <h2
        className="min-w-0 font-semibold tracking-normal"
        style={{
          color: 'var(--form-text,#0F172A)',
          fontFamily: 'var(--form-heading-font,var(--form-font,Geist))',
          fontSize: 'var(--form-question-size,1.25rem)',
          lineHeight: 'var(--form-line-height,1.55)',
        }}
      >
        {question.title}
      </h2>
      <QuestionTitleActions question={question} answers={answers} questions={questions} />
    </div>
  )
}

function QuestionTitleActions({
  question,
  answers,
  questions,
}: {
  question: Question
  answers: Map<string, AnswerValue>
  questions: Question[]
}) {
  const [lookupMessage, setLookupMessage] = useState<{
    text: string
    tone: 'info' | 'success' | 'warning'
  } | null>(null)
  const [duplicateCheckEnabled, setDuplicateCheckEnabled] = useState(false)
  const [duplicateCheckMessage, setDuplicateCheckMessage] = useState<{
    text: string
    tone: 'info' | 'success' | 'warning'
    field: string
    value: string
  } | null>(null)
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false)
  const lastAutoLookupTaxId = useRef<string | null>(null)
  const lookupSettings = question.settings.taxIdLookup
  const lookupTargetQuestionId = lookupSettings?.targetQuestionId
  const taxId = normalizeMiaolinTaxIdInput(String(answers.get(question.id) ?? ''))
  const duplicateCheckSettings = question.settings.customerDuplicateCheck
  const duplicateCheckField = duplicateCheckSettings?.field
  const duplicateCheckPurpose = duplicateCheckField
    ? getCustomerDuplicateCheckPurpose(duplicateCheckField, duplicateCheckSettings?.purpose)
    : 'duplicate_check'
  const duplicateCheckValue = String(answers.get(question.id) ?? '').trim()

  useEffect(() => {
    if (
      !lookupSettings?.enabled ||
      lookupSettings.providerMode !== 'gcis_open_data' ||
      !lookupTargetQuestionId
    ) {
      return
    }

    if (taxId.length !== 8) {
      lastAutoLookupTaxId.current = null
      setFormPlayerAnswerIfChanged(lookupTargetQuestionId, '')
      setLookupMessage(taxId.length > 0 ? { text: '輸入 8 碼後會自動比對。', tone: 'info' } : null)
      return
    }

    if (lastAutoLookupTaxId.current === taxId) return

    const controller = new AbortController()
    let isCurrentLookup = true
    lastAutoLookupTaxId.current = taxId
    setLookupMessage({ text: '正在查詢統編資料...', tone: 'info' })

    void lookupMiaolinTaxIdCompanyTitle(taxId, lookupSettings.providerMode, {
      signal: controller.signal,
    }).then((result) => {
      if (!isCurrentLookup) return
      const latestTaxId = normalizeMiaolinTaxIdInput(
        String(useFormPlayerStore.getState().answers.get(question.id) ?? ''),
      )
      if (latestTaxId !== result.taxId) return

      if (result.status === 'found') {
        setFormPlayerAnswerIfChanged(lookupTargetQuestionId, result.companyTitle)
        setLookupMessage({ text: `已帶入：${result.companyTitle}`, tone: 'success' })
        return
      }

      setFormPlayerAnswerIfChanged(lookupTargetQuestionId, '')
      setLookupMessage({ text: result.message, tone: 'warning' })
    })

    return () => {
      isCurrentLookup = false
      controller.abort()
    }
  }, [
    lookupSettings?.enabled,
    lookupSettings?.providerMode,
    lookupTargetQuestionId,
    question.id,
    taxId,
  ])

  useEffect(() => {
    if (!duplicateCheckSettings?.enabled || !duplicateCheckField) {
      setDuplicateCheckEnabled(false)
      return
    }

    const controller = new AbortController()
    let isCurrentConfig = true

    void fetchMiaolinCustomerDuplicateCheckConfig({ signal: controller.signal }).then((config) => {
      if (!isCurrentConfig) return
      setDuplicateCheckEnabled(config.enabled && config.fields.includes(duplicateCheckField))
    })

    return () => {
      isCurrentConfig = false
      controller.abort()
    }
  }, [duplicateCheckField, duplicateCheckSettings?.enabled])

  const hasTaxIdLookup = Boolean(lookupSettings?.enabled && lookupTargetQuestionId)
  const hideCustomerDuplicateCheck = shouldHideCustomerShortNameDuplicateCheckButton(
    question,
    questions,
    answers,
  )
  const hasCustomerDuplicateCheck = Boolean(
    duplicateCheckSettings?.enabled &&
      duplicateCheckField &&
      duplicateCheckEnabled &&
      !hideCustomerDuplicateCheck,
  )
  if (!hasTaxIdLookup && !hasCustomerDuplicateCheck) return null
  const manualLookupProviderMode = lookupSettings?.providerMode ?? 'synthetic'
  const manualLookupTargetQuestionId = lookupTargetQuestionId ?? ''

  async function compareCustomerDuplicate() {
    if (!duplicateCheckField) return
    const fieldLabel = getCustomerDuplicateCheckFieldLabel(duplicateCheckField)
    setIsCheckingDuplicate(true)
    setDuplicateCheckMessage({
      text: `正在比對${fieldLabel}...`,
      tone: 'info',
      field: duplicateCheckField,
      value: duplicateCheckValue,
    })
    const result = await checkMiaolinCustomerDuplicate({
      field: duplicateCheckField,
      purpose: duplicateCheckPurpose,
      value: duplicateCheckValue,
      formId: question.formId,
      questionId: question.id,
    })
    setIsCheckingDuplicate(false)

    if (
      (duplicateCheckPurpose === 'duplicate_check' && result.status === 'not_found') ||
      (duplicateCheckPurpose === 'existing_lookup' && result.status === 'found')
    ) {
      setDuplicateCheckMessage({
        text: result.message,
        tone: 'success',
        field: duplicateCheckField,
        value: duplicateCheckValue,
      })
      if (duplicateCheckPurpose === 'existing_lookup') {
        const targetQuestionId = resolveExistingCustomerNameTargetQuestionId(question, questions)
        if (targetQuestionId && result.matchedCustomerName) {
          setFormPlayerAnswerIfChanged(targetQuestionId, result.matchedCustomerName)
        }
      }
      return
    }

    if (duplicateCheckPurpose === 'existing_lookup') {
      const targetQuestionId = resolveExistingCustomerNameTargetQuestionId(question, questions)
      if (targetQuestionId) setFormPlayerAnswerIfChanged(targetQuestionId, '')
    }

    setDuplicateCheckMessage({
      text: result.message,
      tone: 'warning',
      field: duplicateCheckField,
      value: duplicateCheckValue,
    })
  }

  async function compareTaxId() {
    const currentValue = answers.get(question.id)
    const result = await lookupMiaolinTaxIdCompanyTitle(
      String(currentValue ?? ''),
      manualLookupProviderMode,
    )

    if (result.status === 'found') {
      setFormPlayerAnswerIfChanged(manualLookupTargetQuestionId, result.companyTitle)
      setLookupMessage({ text: `已帶入：${result.companyTitle}`, tone: 'success' })
      return
    }

    setFormPlayerAnswerIfChanged(manualLookupTargetQuestionId, '')
    setLookupMessage({ text: result.message, tone: 'warning' })
  }

  const visibleDuplicateCheckMessage =
    duplicateCheckMessage &&
    duplicateCheckMessage.field === duplicateCheckField &&
    duplicateCheckMessage.value === duplicateCheckValue
      ? duplicateCheckMessage
      : null

  return (
    <div className="flex shrink-0 flex-col items-start gap-1 sm:items-end">
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        {hasCustomerDuplicateCheck && duplicateCheckField ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label={getCustomerDuplicateCheckActionLabel(
              duplicateCheckField,
              duplicateCheckPurpose,
            )}
            title={getCustomerDuplicateCheckActionLabel(duplicateCheckField, duplicateCheckPurpose)}
            disabled={isCheckingDuplicate}
            onClick={() => void compareCustomerDuplicate()}
          >
            <Search className="size-3.5" />
            {isCheckingDuplicate ? '比對中' : '比對'}
          </Button>
        ) : null}
        {hasTaxIdLookup ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label="比對統一編號財政部資料"
            title="比對統一編號財政部資料"
            onClick={() => void compareTaxId()}
          >
            <Search className="size-3.5" />
            比對
          </Button>
        ) : null}
      </div>
      {visibleDuplicateCheckMessage ? (
        <div
          aria-live="polite"
          data-testid="customer-duplicate-check-message"
          className={cn(
            'max-w-full overflow-x-auto whitespace-nowrap rounded-md border px-2.5 py-1 text-xs font-semibold leading-5 shadow-sm sm:max-w-72',
            visibleDuplicateCheckMessage.tone === 'success' &&
              'border-emerald-200 bg-emerald-50 text-emerald-800',
            visibleDuplicateCheckMessage.tone === 'warning' &&
              'border-amber-300 bg-amber-100 text-amber-950 ring-1 ring-amber-200',
            visibleDuplicateCheckMessage.tone === 'info' && 'border-sky-200 bg-sky-50 text-sky-800',
          )}
        >
          {visibleDuplicateCheckMessage.text}
        </div>
      ) : null}
      {lookupMessage ? (
        <div
          aria-live="polite"
          data-testid="tax-id-lookup-message"
          className={cn(
            'max-w-full overflow-x-auto whitespace-nowrap rounded-md border px-2.5 py-1 text-xs font-semibold leading-5 shadow-sm sm:max-w-72',
            lookupMessage.tone === 'success' && 'border-emerald-200 bg-emerald-50 text-emerald-800',
            lookupMessage.tone === 'warning' &&
              'border-amber-300 bg-amber-100 text-amber-950 ring-1 ring-amber-200',
            lookupMessage.tone === 'info' && 'border-sky-200 bg-sky-50 text-sky-800',
          )}
        >
          {lookupMessage.text}
        </div>
      ) : null}
    </div>
  )
}

function resolveExistingCustomerNameTargetQuestionId(question: Question, questions: Question[]) {
  const configuredTarget = question.settings.existingCustomerNameTargetQuestionId
  return (
    resolveQuestionReference(questions, configuredTarget) ??
    questions.find(
      (candidate) => candidate.settings.customerShortNameSource === 'existing_customer_lookup',
    )?.id ??
    questions.find((candidate) => candidate.settings.sourceQuestionId === 'q-miaolin-05')?.id ??
    null
  )
}

function resolveQuestionReference(questions: Question[], questionReference: string | undefined) {
  if (!questionReference) return null
  return (
    questions.find((question) => question.id === questionReference)?.id ??
    questions.find((question) => question.settings.sourceQuestionId === questionReference)?.id ??
    null
  )
}

function setFormPlayerAnswerIfChanged(questionId: string, value: AnswerValue) {
  const currentValue = useFormPlayerStore.getState().answers.get(questionId)
  if (currentValue === value) return
  useFormPlayerStore.getState().setAnswer(questionId, value)
}

function RequiredQuestionDialog({
  missingQuestion,
  open,
  onOpenChange,
  onGoToQuestion,
}: {
  missingQuestion: { title: string; message: string } | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onGoToQuestion: () => void
}) {
  if (!missingQuestion) return null

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg" data-testid="required-question-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>請補填或重新選擇題目</AlertDialogTitle>
          <AlertDialogDescription>
            {missingQuestion.title}：{missingQuestion.message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm leading-6 text-rose-800">
          已將需要填寫的題目標示出來，按下按鈕後會回到該題。
        </div>
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button type="button" onClick={onGoToQuestion}>
              前往補填
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function MiaolinCustomerCheckDialog({
  result,
  onContinueEditing,
  onConfirmSubmit,
}: {
  result: MiaolinCustomerCheckResult | null
  onContinueEditing: () => void
  onConfirmSubmit: () => void
}) {
  if (!result || (result.status !== 'duplicate_found' && result.status !== 'existing_missing')) {
    return null
  }
  const isDuplicate = result.status === 'duplicate_found'
  const isMissingExisting = result.status === 'existing_missing'
  if (!isDuplicate && !isMissingExisting) return null

  return (
    <AlertDialog open={Boolean(result)} onOpenChange={(open) => !open && onContinueEditing()}>
      <AlertDialogContent className="max-w-lg" data-testid="miaolin-customer-check-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isDuplicate ? '找到可能已存在的客戶資料' : '找不到既有客戶資料'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isDuplicate
              ? '以下欄位已存在於 mock-safe synthetic 客戶資料表。你可以返回編輯，或確認填寫完成並送到 mock 審核流程。'
              : 'mock-safe synthetic 客戶資料表找不到這筆資料，請確認既有客戶代號。'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isDuplicate ? (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-900">
            <div className="font-medium">命中欄位</div>
            <ul className="mt-2 space-y-1">
              {result.matchedFields.map((field) => (
                <li key={`${field.questionId}-${field.label}`}>
                  {field.label}：{field.submittedValue}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm leading-6 text-rose-800">
            <div className="font-medium">查詢欄位</div>
            <dl className="mt-2 grid gap-1">
              {result.lookupFields.map((field) => (
                <div key={field.fieldKey} className="flex flex-wrap gap-1">
                  <dt>{field.label}：</dt>
                  <dd className="font-semibold">{field.submittedValue || '未填寫'}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <AlertDialogFooter className="flex-col sm:flex-row">
          <AlertDialogCancel asChild>
            <Button type="button" variant="outline" onClick={onContinueEditing}>
              我要繼續編輯
            </Button>
          </AlertDialogCancel>
          {isDuplicate ? (
            <AlertDialogAction asChild>
              <Button type="button" onClick={onConfirmSubmit}>
                確認填寫完成
              </Button>
            </AlertDialogAction>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function NewCustomerDuplicateNoticeDialog({
  notice,
  onClose,
}: {
  notice: { title: string; message: string } | null
  onClose: () => void
}) {
  if (!notice) return null

  return (
    <AlertDialog open={Boolean(notice)} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className="max-w-md" data-testid="new-customer-duplicate-notice">
        <AlertDialogHeader>
          <AlertDialogTitle>{notice.title}</AlertDialogTitle>
          <AlertDialogDescription>{notice.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button type="button" onClick={onClose}>
              知道了
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function NewCustomerDuplicateReviewDialog({
  dataTestId,
  dialog,
  title,
  description,
  closeLabel,
  comparedColumnLabel,
  keepButtonLabel,
  onContinueEditing,
  onKeepExisting,
  onConfirmSubmit,
}: {
  dataTestId: string
  dialog: {
    rows: NewCustomerSubmitDuplicateComparisonRow[]
  } | null
  title: string
  description: string
  closeLabel: string
  comparedColumnLabel: string
  keepButtonLabel: string
  onContinueEditing: () => void
  onKeepExisting: () => void
  onConfirmSubmit: () => void
}) {
  if (!dialog) return null

  return (
    <AlertDialog
      open={Boolean(dialog)}
      onOpenChange={(open) => {
        if (!open) onContinueEditing()
      }}
    >
      <AlertDialogContent
        className="m-[10px] flex h-[calc(100vh-20px)] max-h-none w-[calc(100vw-20px)] max-w-none flex-col gap-0 overflow-hidden p-0"
        data-testid={dataTestId}
      >
        <div className="relative border-b bg-white px-6 py-5 pr-16">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-semibold text-slate-950">
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              {description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogCancel asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 text-slate-500 hover:text-slate-900"
              aria-label={closeLabel}
            >
              <X className="size-5" aria-hidden="true" />
            </Button>
          </AlertDialogCancel>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-slate-50 p-6">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="w-full border-collapse text-left text-sm">
              <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700">
                <tr>
                  <th className="w-[24%] border-b border-slate-200 px-4 py-3 font-semibold">
                    欄位
                  </th>
                  <th className="w-[38%] border-b border-slate-200 px-4 py-3 font-semibold">
                    填寫資料
                  </th>
                  <th className="w-[38%] border-b border-slate-200 px-4 py-3 font-semibold">
                    {comparedColumnLabel}
                  </th>
                </tr>
              </thead>
              <tbody>
                {dialog.rows.map((row) => (
                  <tr key={row.field} className="border-b border-slate-100 last:border-b-0">
                    <th className="bg-white px-4 py-3 align-top font-medium text-slate-700">
                      {row.label}
                    </th>
                    <td className="whitespace-pre-wrap px-4 py-3 align-top text-slate-950">
                      {row.submittedValue || '-'}
                    </td>
                    <td className="whitespace-pre-wrap px-4 py-3 align-top text-slate-950">
                      {row.customerValue || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <AlertDialogFooter className="grid grid-cols-[24%_38%_38%] gap-4 border-t bg-white px-6 py-4 sm:space-x-0">
          <AlertDialogCancel asChild>
            <Button type="button" size="lg" variant="outline">
              繼續填寫問卷
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button type="button" size="lg" onClick={onConfirmSubmit}>
              比對後確定送出問卷
            </Button>
          </AlertDialogAction>
          <Button type="button" size="lg" variant="outline" onClick={onKeepExisting}>
            {keepButtonLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function NewCustomerDuplicateKeepExistingConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  onConfirm: () => void
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>取消</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button type="button" onClick={onConfirm}>
              確認
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function resolveNewCustomerDuplicateSignalValue(
  field: NewCustomerSubmitDuplicatePreviewContract['matchedFields'][number] | undefined,
  answers: Map<string, AnswerValue>,
  questions: Question[],
) {
  const request = buildNewCustomerSubmitDuplicatePreviewRequest(questions, answers)
  if (field === 'companyPhone') return request.companyPhone
  if (field === 'contactPhone') return request.contactPhone
  return request.taxId
}

export function getOnePageVisibleQuestions(
  questions: Question[],
  logicJumps: PublicForm['logicJumps'],
  answers: Map<string, AnswerValue>,
  hiddenFields: PublicForm['hiddenFields'],
  questionGroups: QuestionGroupReference[] = [],
  forceVisibleQuestionIds: Set<string> = new Set(),
) {
  const firstQuestion = questions[0]
  if (!firstQuestion) return []

  const questionById = new Map(questions.map((question) => [question.id, question]))
  const visibleQuestions: Question[] = []
  const visitedQuestionIds = new Set<string>()
  let currentQuestionId: string | null = firstQuestion.id
  const maxSteps = questions.length + logicJumps.length + 5

  for (let step = 0; currentQuestionId && step < maxSteps; step += 1) {
    const currentQuestion = questionById.get(currentQuestionId)
    if (!currentQuestion || visitedQuestionIds.has(currentQuestion.id)) break
    visibleQuestions.push(currentQuestion)
    visitedQuestionIds.add(currentQuestion.id)
    currentQuestionId = getNextQuestionId(
      currentQuestion.id,
      questions,
      logicJumps,
      answers,
      hiddenFields,
      questionGroups,
    )
  }

  const controlledQuestionIds = collectDisplayTargetQuestionIds(
    logicJumps,
    questions,
    questionGroups,
  )
  if (controlledQuestionIds.size === 0) {
    return visibleQuestions.filter((question) =>
      shouldShowByConditionalDisplay(question, answers, forceVisibleQuestionIds),
    )
  }

  const activeQuestionIds = collectDisplayTargetQuestionIds(
    logicJumps,
    questions,
    questionGroups,
    (jump) => evaluateLogicJump(jump, answers, hiddenFields),
  )

  return visibleQuestions
    .filter(
      (question) => !controlledQuestionIds.has(question.id) || activeQuestionIds.has(question.id),
    )
    .filter((question) =>
      shouldShowByConditionalDisplay(question, answers, forceVisibleQuestionIds),
    )
}

function shouldShowByConditionalDisplay(
  question: Question,
  answers: Map<string, AnswerValue>,
  forceVisibleQuestionIds: Set<string> = new Set(),
) {
  if (
    shouldHideEmptyMiaolinDynamicChannelTypeQuestion(question) &&
    !forceVisibleQuestionIds.has(question.id)
  ) {
    return false
  }

  return shouldShowQuestionByConditionalDisplay(question, answers)
}
